import { useSyncExternalStore } from "react"
import type { QueueItem } from "@/lib/review/queue"

interface ReviewState {
  queue: QueueItem[]
  cursor: number
  current: QueueItem | null
  view: "focus" | "table"
  selected: Set<string>
}

let state: ReviewState = {
  queue: [],
  cursor: 0,
  current: null,
  view: "focus",
  selected: new Set(),
}
const listeners = new Set<() => void>()

function set(next: Partial<ReviewState>) {
  const merged = { ...state, ...next }
  merged.current = merged.queue[merged.cursor] ?? null
  state = merged
  listeners.forEach((l) => l())
}

export const reviewActions = {
  setQueue: (queue: QueueItem[]) =>
    set({ queue, cursor: 0, selected: new Set() }),
  next: () =>
    set({
      cursor: Math.min(state.cursor + 1, Math.max(0, state.queue.length - 1)),
    }),
  prev: () => set({ cursor: Math.max(state.cursor - 1, 0) }),
  goto: (i: number) =>
    set({ cursor: Math.max(0, Math.min(i, state.queue.length - 1)) }),
  completeCurrent: () => {
    const queue = state.queue.filter((_, i) => i !== state.cursor)
    set({
      queue,
      cursor: Math.min(state.cursor, Math.max(0, queue.length - 1)),
    })
  },
  insertAt: (index: number, item: QueueItem) => {
    const i = Math.max(0, Math.min(index, state.queue.length))
    const queue = [...state.queue.slice(0, i), item, ...state.queue.slice(i)]
    set({ queue, cursor: i })
  },
  reset: () => set({ queue: [], cursor: 0, selected: new Set() }),
  setView: (view: ReviewState["view"]) => set({ view }),
  toggleSelected: (key: string) => {
    const selected = new Set(state.selected)
    if (selected.has(key)) selected.delete(key)
    else selected.add(key)
    set({ selected })
  },
  selectKeys: (keys: string[], on: boolean) => {
    const selected = new Set(state.selected)
    keys.forEach((k) => (on ? selected.add(k) : selected.delete(k)))
    set({ selected })
  },
  clearSelected: () => set({ selected: new Set() }),
  completeKeys: (keys: string[]) => {
    const drop = new Set(keys)
    const queue = state.queue.filter((i) => !drop.has(i.key))
    const selected = new Set(state.selected)
    keys.forEach((k) => selected.delete(k))
    set({
      queue,
      selected,
      cursor: Math.min(state.cursor, Math.max(0, queue.length - 1)),
    })
  },
}

export function getReviewState(): ReviewState {
  return state
}

export function useReview(): ReviewState {
  return useSyncExternalStore(
    (cb) => {
      listeners.add(cb)
      return () => listeners.delete(cb)
    },
    getReviewState,
    getReviewState
  )
}
